import { DeliveryType } from "@/app/types/delivery";
import { PaymentMethod } from "@/app/types/payment";
import { ProductDetailType } from "@/app/types/product-site";
import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { CartPriceChanges, CartType, ClientCartItemType } from "../../../types/cart";

export type PriceChangedItem = {
	product_name: string;
	image: string | null;
	old_price: number;
	new_price: number;
};

export type PaymentType = "portal" | "c2c" | "cash";

type CartState = {
	serverCart: CartType | null;
	clientCart: {
		items: ClientCartItemType[];
		paymentMethod: PaymentMethod | null;
		paymentType: PaymentType;
	};
	loading: boolean;
	submitLoading: boolean;
	fetchingCart: boolean;
	redirectingToGateway: boolean;
	priceChangedItems: PriceChangedItem[] | null;
	priceChanges: CartPriceChanges | null;
};

const initialState: CartState = {
	serverCart: null,
	clientCart: {
		items: [],
		paymentMethod: null,
		paymentType: "portal",
	},
	loading: false,
	fetchingCart: false,
	submitLoading: false,
	redirectingToGateway: false,
	priceChangedItems: null,
	priceChanges: null,
};

const cartSlice = createSlice({
	name: "cart",
	initialState,

	reducers: {
		startFetching(state) {
			state.fetchingCart = true;
		},
		stopFetching(state) {
			state.fetchingCart = false;
		},
		startLoading(state) {
			state.loading = true;
		},
		stopLoading(state) {
			state.loading = false;
		},
		startSubmitLoading(state) {
			state.submitLoading = true;
		},
		stopSubmitLoading(state) {
			state.submitLoading = false;
		},
		startRedirectingToGateway(state) {
			state.redirectingToGateway = true;
		},
		stopRedirectingToGateway(state) {
			state.redirectingToGateway = false;
		},
		updateCart(state, action: PayloadAction<CartType>) {
			state.serverCart = action.payload;

			const key = "server_cart";
			const oldData = localStorage.getItem(key);
			const newData = JSON.stringify(action.payload);
			if (oldData !== newData) {
				const payload = action.payload ? JSON.stringify(action.payload) : null;
				localStorage.setItem(key, payload);
			}
		},
		clearClientCart(state) {
			state.clientCart = {
				items: [],
				paymentMethod: null,
				paymentType: "portal",
			};

			localStorage.removeItem("client_cart");
		},
		setPaymentMethod(state, action: PayloadAction<PaymentMethod>) {
			state.clientCart.paymentMethod = action.payload;
		},
		setPaymentType(state, action: PayloadAction<PaymentType>) {
			state.clientCart.paymentType = action.payload;
		},
		clearServerCart(state) {
			state.serverCart = null;
		},
		clearCart(state) {
			//Remove client cart
			state.clientCart = {
				items: [],
				paymentMethod: null,
				paymentType: "portal",
			};
			//Remove server cart
			state.serverCart = null;
			state.fetchingCart = false;
			localStorage.removeItem("client_cart");
		},
		addToClientCart(state, action: PayloadAction<ProductDetailType>) {
			const prevClientCartItemIndex = state.clientCart?.items?.findIndex((item) => item?.product?.product_id === action?.payload?.product_id);

			if (prevClientCartItemIndex >= 0) {
				const prevItem = state.clientCart.items[prevClientCartItemIndex];

				//Add qty to prev one
				state.clientCart.items[prevClientCartItemIndex] = {
					product: action.payload,
					qty: prevItem?.qty + 1,
				};
			} else {
				state.clientCart.items = [
					...state.clientCart.items,
					{
						product: action.payload,
						qty: 1,
					},
				];
			}
			localStorage.setItem("client_cart", JSON.stringify(state.clientCart));
		},
		removeFromClientCart(state, action: PayloadAction<ProductDetailType>) {
			const prevClientCartItemIndex = state.clientCart?.items?.findIndex((item) => item?.product?.product_id === action?.payload?.product_id);

			if (prevClientCartItemIndex >= 0) {
				const prevItem = state.clientCart.items[prevClientCartItemIndex];

				const newQty = prevItem?.qty - 1;

				if (newQty === 0) {
					//Should remove product
					state.clientCart.items = state.clientCart.items.filter((_, i) => i !== prevClientCartItemIndex);
				} else {
					state.clientCart.items[prevClientCartItemIndex] = {
						product: action.payload,
						qty: prevItem?.qty - 1,
					};
				}
			} else {
				state.clientCart.items.filter((item) => item?.product?.product_id !== action?.payload?.product_id);
			}

			localStorage.setItem("client_cart", JSON.stringify(state.clientCart));
		},
		setPriceChangedItems(state, action: PayloadAction<{ items: PriceChangedItem[]; changes: CartPriceChanges }>) {
			state.priceChangedItems = action.payload.items;
			state.priceChanges = action.payload.changes;
		},
		clearPriceChangedItems(state) {
			state.priceChangedItems = null;
			state.priceChanges = null;
		},
		updateClientCart(state, action: PayloadAction<any>) {
			state.clientCart = action.payload;
		},
		updateCartDeliveryMethod(state, action: PayloadAction<DeliveryType>) {
			const minPrice = action.payload.min_price_to_skip;
			const isFreeDelivery = state.serverCart?.total_price >= minPrice;
			let finalAmount = action.payload.price;
			if (isFreeDelivery) finalAmount = 0;
			const cartPricing = state?.serverCart?.pricing;
			let findedDelivery = cartPricing.find((p) => p.key === "delivery");
			const deliveryIndex = cartPricing.findIndex((item) => item.key === "delivery");
			if (findedDelivery) {
				findedDelivery = {
					...findedDelivery,
					amount: finalAmount,
				};
				cartPricing[deliveryIndex] = findedDelivery;
			}
			state.serverCart.pricing = cartPricing;
		},
	},
});

export const {
	updateCart,
	clearCart,
	clearClientCart,
	clearServerCart,
	addToClientCart,
	removeFromClientCart,
	setPaymentMethod,
	setPaymentType,
	startLoading,
	updateCartDeliveryMethod,
	startSubmitLoading,
	stopSubmitLoading,
	stopLoading,
	startFetching,
	stopFetching,
	startRedirectingToGateway,
	stopRedirectingToGateway,
	updateClientCart,
	setPriceChangedItems,
	clearPriceChangedItems,
} = cartSlice.actions;

export default cartSlice;
